import { PaletteColor, ThemeVariable } from '@spaces-ui/theme';

/** An sRGB color with an alpha channel, parsed from a CSS color literal. */
export interface ParsedColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

const HEX_COLOR_REGEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/;
const RGB_FUNCTION_REGEX =
  /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})(?:\s*[,/]\s*(\d*\.?\d+%?))?\s*\)$/;
const VAR_REFERENCE_REGEX = /^var\(\s*(--[\w-]+)\s*(?:,\s*(.+))?\)$/;

/**
 * Parse a CSS color literal into channels. Handles the forms the compiled theme
 * stylesheet actually contains: `#rgb`/`#rgba`/`#rrggbb`/`#rrggbbaa` and
 * `rgb()`/`rgba()` in comma, minified, and space/slash syntax. Anything else
 * (keywords, `var()` remnants, non-colors) returns `null`.
 */
export function parseCssColor(value: string): ParsedColor | null {
  const normalized = value.trim().toLowerCase();

  const hexMatch = HEX_COLOR_REGEX.exec(normalized);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length <= 4) {
      hex = hex
        .split('')
        .map(digit => digit + digit)
        .join('');
    }
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
    };
  }

  const rgbMatch = RGB_FUNCTION_REGEX.exec(normalized);
  if (rgbMatch) {
    let alpha = 1;
    if (rgbMatch[4] !== undefined) {
      alpha = rgbMatch[4].endsWith('%')
        ? parseFloat(rgbMatch[4]) / 100
        : parseFloat(rgbMatch[4]);
    }
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: alpha,
    };
  }

  return null;
}

/**
 * Index the palette by `r,g,b` channels. Palette hexes are read back from
 * `background-color` declarations, which browsers re-serialize (e.g. `#f5f5f4`
 * becomes `rgb(245, 245, 244)`), so both sides of the reverse-map must be
 * normalized through {@link parseCssColor} rather than string-compared.
 */
export function buildPaletteIndex(palette: PaletteColor[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const { name, hex } of palette) {
    const color = parseCssColor(hex);
    if (!color) {
      continue;
    }
    const key = `${color.r},${color.g},${color.b}`;
    if (!index.has(key)) {
      index.set(key, name);
    }
  }
  return index;
}

/** A theme's composed variables as a lookup map. */
export function toVarMap(variables: ThemeVariable[]): Map<string, string> {
  return new Map(variables.map(({ cssVar, value }) => [cssVar, value]));
}

/**
 * Derive a display label for a token's value in one theme: the palette color
 * name (`"N600"`), with an opacity suffix when translucent (`"N900 @ 50%"`).
 *
 * `var()` references are resolved through the composed variable map first.
 * Values that can't be resolved to a palette color (off-palette ramp colors,
 * non-color values, unresolvable `var()`s) fall back to the raw value so the
 * page still shows something truthful. Returns `null` only when the token is
 * absent from the map entirely (rendered as the N/D badge).
 */
export function deriveTokenLabel(
  cssVar: string,
  vars: Map<string, string>,
  paletteIndex: Map<string, string>,
): string | null {
  let value = vars.get(cssVar);
  if (value === undefined) {
    return null;
  }

  const visited = new Set([cssVar]);
  let reference = VAR_REFERENCE_REGEX.exec(value.trim());
  while (reference) {
    const referencedVar = reference[1];
    if (visited.has(referencedVar)) {
      return value;
    }
    visited.add(referencedVar);
    const next = vars.get(referencedVar) ?? reference[2];
    if (next === undefined) {
      return value;
    }
    value = next;
    reference = VAR_REFERENCE_REGEX.exec(value.trim());
  }

  const color = parseCssColor(value);
  if (!color) {
    return value;
  }

  const name = paletteIndex.get(`${color.r},${color.g},${color.b}`);
  if (name === undefined) {
    return value;
  }

  if (color.a >= 1) {
    return name;
  }
  return `${name} @ ${Math.round(color.a * 100)}%`;
}
